import { useMemo, useState } from "react";
import { X } from "lucide-react";

// Two-hour service windows — labels match the dispatcher + booking wizard.
const TIME_BLOCKS = [
  { startHour: 8,  label: "8 – 10 AM" },
  { startHour: 10, label: "10 AM – 12 PM" },
  { startHour: 12, label: "12 – 2 PM" },
  { startHour: 14, label: "2 – 4 PM" },
  { startHour: 16, label: "4 – 6 PM" },
  { startHour: 18, label: "6 – 8 PM" },
];

const WEEKDAY = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/**
 * 14-day horizontal date strip + 6 two-hour blocks below.
 * Outputs an ISO string with the START hour of the chosen 2-hour block.
 *
 * Props:
 *   value      — ISO 8601 string (or "" / null for unset)
 *   onChange   — (iso: string) => void
 *   testidPrefix — string prefix for data-testid attributes (default "schedule")
 */
export default function SchedulePicker({ value, onChange, testidPrefix = "schedule" }) {
  const today = useMemo(() => {
    const d = new Date(); d.setHours(0, 0, 0, 0); return d;
  }, []);
  const days = useMemo(() => Array.from({ length: 14 }, (_, i) => {
    const d = new Date(today); d.setDate(d.getDate() + i); return d;
  }), [today]);

  const parsed = useMemo(() => {
    if (!value) return { dateKey: null, hour: null };
    try {
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return { dateKey: null, hour: null };
      return {
        dateKey: `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`,
        hour: d.getHours(),
      };
    } catch { return { dateKey: null, hour: null }; }
  }, [value]);

  const [activeDate, setActiveDate] = useState(() => parsed.dateKey ? new Date(value) : null);
  const activeDateKey = activeDate
    ? `${activeDate.getFullYear()}-${activeDate.getMonth()}-${activeDate.getDate()}`
    : null;

  function pickDate(d) {
    setActiveDate(d);
    if (parsed.hour != null) {
      const out = new Date(d); out.setHours(parsed.hour, 0, 0, 0);
      onChange(out.toISOString());
    }
  }
  function pickBlock(startHour) {
    const base = activeDate || today;
    const out = new Date(base); out.setHours(startHour, 0, 0, 0);
    onChange(out.toISOString());
    if (!activeDate) setActiveDate(out);
  }
  function clear() { onChange(""); setActiveDate(null); }

  return (
    <div
      className="mt-2 border border-[#002060]/15 rounded-lg p-3 bg-[#FFFFFF]"
      data-testid={`${testidPrefix}-picker`}
    >
      {/* Date strip */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-thin">
        {days.map((d) => {
          const dk = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
          const isToday = dk === `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
          const selected = dk === activeDateKey;
          return (
            <button
              key={dk}
              type="button"
              data-testid={`${testidPrefix}-date-${dk}`}
              onClick={() => pickDate(d)}
              className={`shrink-0 w-14 h-16 rounded-lg border-2 flex flex-col items-center justify-center transition-all ${
                selected
                  ? "border-[#FF6600] bg-[#FF6600] text-[#FFFFFF]"
                  : "border-[#002060]/15 bg-[#FFFFFF] text-[#002060] hover:border-[#002060]/40"
              }`}
            >
              <span className={`text-[10px] uppercase tracking-widest font-semibold ${selected ? "text-[#FFFFFF]/90" : "text-[#002060]/60"}`}>
                {WEEKDAY[d.getDay()]}
              </span>
              <span className="text-lg font-bold leading-none mt-0.5 num">{d.getDate()}</span>
              {isToday && (
                <span className={`text-[8px] uppercase tracking-widest font-bold mt-0.5 ${selected ? "text-[#FFFFFF]/90" : "text-[#FF6600]"}`}>
                  Today
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Block grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
        {TIME_BLOCKS.map((b) => {
          const selected = activeDateKey && parsed.hour === b.startHour;
          return (
            <button
              key={b.startHour}
              type="button"
              data-testid={`${testidPrefix}-slot-${b.startHour}`}
              onClick={() => pickBlock(b.startHour)}
              className={`h-11 rounded-lg border-2 text-sm font-semibold transition-all ${
                selected
                  ? "border-[#FF6600] bg-[#FF6600]/10 text-[#FF6600]"
                  : "border-[#002060]/15 bg-[#FFFFFF] text-[#002060] hover:border-[#002060]/40"
              }`}
            >
              {b.label}
            </button>
          );
        })}
      </div>

      {/* Summary + clear */}
      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="text-xs text-[#002060]/70" data-testid={`${testidPrefix}-summary`}>
          {value ? (
            <>
              <span className="font-semibold text-[#002060]">Scheduled:</span>{" "}
              {new Date(value).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
              {" · "}
              {TIME_BLOCKS.find((b) => b.startHour === new Date(value).getHours())?.label ||
                new Date(value).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
            </>
          ) : (
            "No time chosen — job will stay unscheduled."
          )}
        </div>
        {value && (
          <button
            type="button"
            data-testid={`${testidPrefix}-clear`}
            onClick={clear}
            className="text-xs text-[#FF6600] font-semibold hover:underline flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
      </div>
    </div>
  );
}

export { TIME_BLOCKS };
